// The quest source's data side: a game's quest rows (the layout spike 002's `quests` table has: names and labels as
// string references, objectives counting deeds or waiting on a condition, a marker each, actions as flat objects) read
// into @rpg/story's QuestDefs, and a standard QuestSource over them. A generated or AI quest source plugs in behind the
// same QuestSource instead (decision 0055).
import { staticQuests, type FactValue, type QuestDef, type QuestSource, type StoryAction } from "@rpg/story";

/** A reference into a strings table. */
export interface StringRef {
  table: string;
  id: string;
}

/** An action as a row writes it: one verb key and its fields (the story checks the combinations). */
export type ActionRow = Readonly<Record<string, unknown>>;

/** One objective as a row writes it. */
export interface ObjectiveRow {
  id: string;
  label: StringRef;
  on?: { kind: string; subject?: string | readonly string[]; place?: string | readonly string[]; tag?: string | readonly string[]; count?: number };
  when?: string;
  /** Where the marker points: "npc:<id>", "region:<id>", "group:<id>", "place:<id>" (the game resolves each kind). */
  marker?: string;
  start?: readonly ActionRow[];
  done?: readonly ActionRow[];
}

/** One quest as a row writes it. */
export interface QuestRow {
  id: string;
  kind: string;
  name: StringRef;
  summary?: StringRef;
  giver?: string;
  offer?: string;
  autoStart?: boolean;
  fail?: string;
  parallel?: boolean;
  objectives: readonly ObjectiveRow[];
  start?: readonly ActionRow[];
  rewards?: readonly ActionRow[];
  failed?: readonly ActionRow[];
}

/** A row's action back to @rpg/story's union (the first verb key found decides). Throws on a row with no verb. */
export function readAction(a: ActionRow): StoryAction {
  const r = a as Record<string, unknown>;
  if ("set" in r) return { set: r.set as string, value: (r.value ?? null) as FactValue };
  if ("add" in r) return { add: r.add as string, amount: r.amount as number };
  if ("quest" in r) return { quest: r.quest as string, state: r.state as "offered" | "active" | "done" | "failed" };
  if ("affinity" in r) return { affinity: r.affinity as string, amount: r.amount as number };
  if ("standing" in r) return { standing: r.standing as string, amount: r.amount as number };
  if ("remember" in r) return { remember: r.remember as string, key: r.key as string, value: (r.value ?? null) as FactValue };
  if ("arc" in r) return { arc: r.arc as string, beat: r.beat as string };
  if ("host" in r) return { host: r.host as string, ...(r.args ? { args: r.args as Record<string, FactValue> } : {}) };
  throw new Error(`story: an action with no verb: ${JSON.stringify(a)}`);
}

const actions = (list: readonly ActionRow[]): StoryAction[] => list.map(readAction);

/** Quest rows as @rpg/story's QuestDefs (titles, summaries and labels as string ids). */
export function readQuests(rows: readonly QuestRow[]): QuestDef[] {
  return rows.map((q) => ({
    id: q.id,
    kind: q.kind,
    title: q.name.id,
    ...(q.summary ? { summary: q.summary.id } : {}),
    ...(q.giver ? { giver: q.giver } : {}),
    ...(q.offer ? { offer: q.offer } : {}),
    ...(q.autoStart !== undefined ? { autoStart: q.autoStart } : {}),
    ...(q.fail ? { fail: q.fail } : {}),
    ...(q.parallel !== undefined ? { parallel: q.parallel } : {}),
    objectives: q.objectives.map((o) => ({
      id: o.id,
      label: o.label.id,
      ...(o.on ? { on: { kind: o.on.kind, ...(o.on.subject !== undefined ? { subject: o.on.subject } : {}), ...(o.on.place !== undefined ? { place: o.on.place } : {}), ...(o.on.tag !== undefined ? { tag: o.on.tag } : {}), ...(o.on.count !== undefined ? { count: o.on.count } : {}) } } : {}),
      ...(o.when ? { when: o.when } : {}),
      ...(o.marker ? { marker: o.marker } : {}),
      ...(o.start ? { start: actions(o.start) } : {}),
      ...(o.done ? { done: actions(o.done) } : {}),
    })),
    ...(q.start ? { start: actions(q.start) } : {}),
    ...(q.rewards ? { rewards: actions(q.rewards) } : {}),
    ...(q.failed ? { failed: actions(q.failed) } : {}),
  }));
}

/** The standard quest source over a game's rows (@rpg/story's `staticQuests`). */
export function questSource(rows: readonly QuestRow[]): QuestSource {
  return staticQuests(readQuests(rows));
}
